/* eslint-disable react/jsx-props-no-spreading */
import {
  Box,
  Grid,
  Autocomplete,
  Checkbox,
  Chip,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import * as Sentry from "@sentry/react";
import AutoCompleteDropdown from "../../../components/common/new/AutoCompleteDropdown/AutoCompleteDropdown";
import DateRangePicker from "../../../components/common/DateRangePicker/DateRangePicker";
import useParamsQuery from "../../../hooks/useParamsQuery";
import {
  SET_MAINLINE_POSITIONS,
  SET_DUAL_POSITIONS,
  SET_ROLES,
} from "../../../redux/actionTypes/meta/newMeta";
import IntroductionForm from "../../../services/api/introductionForm";
import User from "../../../services/api/user";
import { isEmptyObj } from "../../../utils/helper";
import { getOptions } from "../../../utils";
import { SET_PROFESSIONS } from "../../../redux/actionTypes/meta";

const MAINLINE_POSITION = "MAINLINE_POSITION";
const DEPUTY_POSITION = "DEPUTY_POSITION";

const initialFormValue = {
  dateOfBirthFrom: "",
  dateOfBirthTo: "",
  dateOfJoiningFrom: "",
  dateOfJoiningTo: "",
  appointedAtFrom: "",
  appointedAtTo: "",
  conferralDateFrom: "",
  conferralDateTo: "",
  honoraryDistrictChief: false,
};

// const removeRoles = [
//   "helpDeskAdmin",
//   "areaDeputySI",
//   "chapterDeputySI",
//   "districtDeputySI",
//   "emailManager",
//   "helpDesk",
//   "member",
//   "nationalLeader",
//   "regionDeputySI",
//   "zoneDeputySI",
// ];

function AdvancedSearch() {
  const [initialAdvanceFormValue, setInitialAdvanceFormValue] =
    useState(initialFormValue);
  const { getQueryObject, getQueryParams, urlSearchParams } = useParamsQuery();

  const [positions, setPositions] = useState([]);
  const [mainlinePositions, setMainlinePositions] = useState([]);
  const [selectedPositions, setSelectedPositions] = useState([]);
  const [selectedMainlinePositions, setSelectedMainlinePositions] = useState(
    []
  );
  const [selectedGohonzonTypes, setSelectedGohonzonTypes] = useState([]);
  const [profession, setProfession] = useState([]);
  const [selectedProfession, setSelectedProfession] = useState("");
  const [roles, setRoles] = useState([]);
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [honoraryDistrictChief, setHonoraryDistrictChief] = useState(false);
  const [specialGroupAppointments, setSpecialGroupAppointments] =
    useState(false);
  const dispatch = useDispatch();

  const gohonzonTypeOptions = [
    { entityTitle: "Okataki gohonzon", value: "OKATAKI_GOHONZON" },
    { entityTitle: "Omamori gohonzon", value: "OMAMORI_GOHONZON" },
  ];

  useEffect(() => {
    const queryObject = getQueryObject();
    if (!isEmptyObj(queryObject)) {
      const query = getQueryParams(queryObject)[0];
      Object.keys(initialFormValue).forEach((key) => {
        setInitialAdvanceFormValue((prev) => ({
          ...prev,
          [key]: query[key] ?? "",
        }));
      });

      setHonoraryDistrictChief(query.honoraryDistrictChief === "true");
      setSpecialGroupAppointments(query.specialGroupAppointments === "true");
      setSelectedGohonzonTypes(query.gohonzonTypes?.split(",") || []);
    } else {
      setInitialAdvanceFormValue(initialFormValue);
      setSelectedPositions([]);
      setSelectedMainlinePositions([]);
      setSelectedRoles([]);
      setHonoraryDistrictChief(false);
      setSpecialGroupAppointments(false);
      setSelectedGohonzonTypes([]);
    }
  }, [urlSearchParams.toString()]);

  const getPositions = async () => {
    try {
      const [mainlinePositionsRes, othersPositionRes] = await Promise.all([
        IntroductionForm.getAllPositions(MAINLINE_POSITION),
        IntroductionForm.getAllPositions(DEPUTY_POSITION),
      ]);
      const mainlineFormattedPos =
        mainlinePositionsRes?.data?.map((d) => ({
          entityTitle: d.name,
          value: d.positionId,
        })) ?? [];
      const othersFormattedPos =
        othersPositionRes?.data?.map((d) => ({
          entityTitle: d.name,
          value: d.positionId,
        })) ?? [];

      setMainlinePositions(mainlineFormattedPos);
      setPositions(othersFormattedPos);
      dispatch({ type: SET_MAINLINE_POSITIONS, payload: mainlineFormattedPos });
      dispatch({ type: SET_DUAL_POSITIONS, payload: othersFormattedPos });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const getProfessions = async () => {
    const response = await IntroductionForm.getProfessions();
    setProfession(response.data);
    dispatch({ type: SET_PROFESSIONS, payload: response.data });
  };

  const getRoles = async () => {
    try {
      const rolesRes = await User.getRole();
      // const filteredRoles =
      //   rolesRes?.data
      //     ?.map((d) => ({ entityTitle: d.name, value: d.roleId }))
      //     .filter((r) => !removeRoles.includes(r.entityTitle)) ?? [];

      const filteredRoles =
        rolesRes?.data
          ?.map((d) => ({
            entityTitle: d.name?.trim?.() ?? d.name,
            value: d.roleId,
          }))
          .filter((r) => {
            const name = (r.entityTitle ?? "").trim();
            const lower = name.toLowerCase();

            const isSI = name.endsWith("SI");
            const isDeputySI =
              name.endsWith("DeputySI") || lower.includes("deputysi");
            const isLeader = lower.includes("leader");
            const isAdmin = lower.includes("admin");
            const isDataEntry = lower.includes("dataentry");

            return isSI && !isDeputySI && !isLeader && !isAdmin && !isDataEntry;
          }) ?? [];

      setRoles(filteredRoles);
      dispatch({ type: SET_ROLES, payload: filteredRoles });
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  useEffect(() => {
    getPositions();
    getProfessions();
    getRoles();
  }, []);

  return (
    <Box
      sx={{
        width: "100%",
        pr: { xs: "20px", sm: 0 },
        "& .MuiInputLabel-root": {
          fontSize: "12px",
          fontWeight: 600,
          lineHeight: "11px",
          paddingBottom: "2px",
        },
        "& .MuiOutlinedInput-input": {
          height: "14px",
          padding: "10.5px",
        },
      }}
    >
      <Grid
        container
        spacing={2}
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            sm: "1fr 1fr",
            md: "repeat(3, 1fr)",
          },
          rowGap: 2,
          columnGap: 2,
        }}
      >
        {/* Group 1 - Date Fields */}
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            boxShadow: "1px 0 2px 0 #e7e2e2",
          }}
        >
          <DateRangePicker
            label="Date of Birth"
            name="dateOfBirth"
            startDateVal={initialAdvanceFormValue.dateOfBirthFrom}
            endDateVal={initialAdvanceFormValue.dateOfBirthTo}
          />
          <DateRangePicker
            label="Date of Joining"
            name="dateOfJoining"
            startDateVal={initialAdvanceFormValue.dateOfJoiningFrom}
            endDateVal={initialAdvanceFormValue.dateOfJoiningTo}
          />
          <DateRangePicker
            label="Appointment Date"
            name="appointedAt"
            startDateVal={initialAdvanceFormValue.appointedAtFrom}
            endDateVal={initialAdvanceFormValue.appointedAtTo}
          />
        </Grid>

        {/* Group 2 - Positions, Roles */}
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            boxShadow: "1px 0 2px 0 #e7e2e2",
          }}
        >
          <AutoCompleteDropdown
            getOptionLabel={(option) => option.profession}
            options={getOptions(profession, "professionsId")}
            label="Profession"
            name="professionsId"
            value={selectedProfession}
            onChange={(selectedValue) => {
              setSelectedProfession(selectedValue?.value);
            }}
            inputProps={{ required: false }}
            useHiddenInput
          />
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={roles}
            getOptionLabel={(option) => option.entityTitle}
            value={roles.filter((role) => selectedRoles.includes(role.value))}
            onChange={(e, newValue) => {
              const selected = newValue.map((item) => item.value);
              setSelectedRoles(selected);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 5, padding: 0 }}
                  checked={selected}
                />
                {option.entityTitle}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.value}
                  label={option.entityTitle}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              // <TextField {...params} label="Role" placeholder="Select..." />
              <TextField
                {...params}
                label="Statistics Incharges"
                placeholder="Select..."
              />
            )}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                paddingTop: selectedRoles.length === 0 ? "0px" : "auto",
              },
            }}
          />
          <input type="hidden" name="roleIds" value={selectedRoles.join(",")} />
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={mainlinePositions}
            getOptionLabel={(option) => option.entityTitle}
            value={mainlinePositions.filter((pos) =>
              selectedMainlinePositions.includes(pos.value)
            )}
            onChange={(e, newValue) => {
              const selected = newValue.map((item) => item.value);
              setSelectedMainlinePositions(selected);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 5, padding: 0 }}
                  checked={selected}
                />
                {option.entityTitle}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.value}
                  label={option.entityTitle}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Mainline Leadership Position"
                placeholder="Select..."
              />
            )}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                paddingTop: selectedRoles.length === 0 ? "0px" : "auto",
              },
            }}
          />
          <input
            type="hidden"
            name="mainlinePositionIds"
            value={selectedMainlinePositions.join(",")}
          />

          <Autocomplete
            multiple
            disableCloseOnSelect
            options={positions}
            getOptionLabel={(option) => option.entityTitle}
            value={positions.filter((pos) =>
              selectedPositions.includes(pos.value)
            )}
            onChange={(e, newValue) => {
              const selected = newValue.map((item) => item.value);
              setSelectedPositions(selected);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 5, padding: 0 }}
                  checked={selected}
                />
                {option.entityTitle}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.value}
                  label={option.entityTitle}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Other Positions"
                placeholder="Select..."
              />
            )}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                paddingTop: selectedRoles.length === 0 ? "0px" : "auto",
              },
            }}
          />
          <input
            type="hidden"
            name="dualPositionIds"
            value={selectedPositions.join(",")}
          />
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            <Checkbox
              name="honoraryDistrictChief"
              value={honoraryDistrictChief}
              checked={honoraryDistrictChief}
              onChange={(e) => setHonoraryDistrictChief(e.target.checked)}
              sx={{ padding: "0px" }}
            />
            <span>Honorary District Chief</span>
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1,
              fontSize: "13px",
              fontWeight: 600,
            }}
          >
            <Checkbox
              name="specialGroupAppointments"
              value={specialGroupAppointments}
              checked={specialGroupAppointments}
              onChange={(e) => setSpecialGroupAppointments(e.target.checked)}
              sx={{ padding: "0px" }}
            />
            <span>Special Group Appointments</span>
          </Box>
        </Grid>

        {/* Group 3 - Gohonzon + Conferral Date */}
        <Grid
          item
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 3,
            padding: 1,
            boxShadow: "1px 0 2px 0 #e7e2e2",
          }}
        >
          <Autocomplete
            multiple
            disableCloseOnSelect
            options={gohonzonTypeOptions}
            getOptionLabel={(option) => option.entityTitle}
            value={gohonzonTypeOptions.filter((opt) =>
              selectedGohonzonTypes.includes(opt.value)
            )}
            onChange={(event, newValue) => {
              const selected = newValue.map((item) => item.value);
              setSelectedGohonzonTypes(selected);
            }}
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  style={{ marginRight: 5, padding: 0 }}
                  checked={selected}
                />
                {option.entityTitle}
              </li>
            )}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.value}
                  label={option.entityTitle}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={(params) => (
              <TextField
                {...params}
                label="Gohonzon Type"
                placeholder="Select..."
              />
            )}
            sx={{
              "& .MuiAutocomplete-inputRoot": {
                paddingTop: selectedGohonzonTypes.length === 0 ? "0px" : "auto",
              },
            }}
          />
          <input
            type="hidden"
            name="gohonzonTypes"
            value={selectedGohonzonTypes.join(",")}
          />
          <DateRangePicker
            label="Conferral Date"
            name="conferralDate"
            startDateVal={initialAdvanceFormValue.conferralDateFrom}
            endDateVal={initialAdvanceFormValue.conferralDateTo}
          />
        </Grid>
      </Grid>
    </Box>
  );
}

export default AdvancedSearch;
